import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxDropdownOptionsSource } from '@onyx/angular';
import {
  DropdownHelper,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxToastService,
} from '@onyx/angular';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { chain } from 'lodash';
import { combineLatestWith, map, startWith, Subject } from 'rxjs';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { FleetIdentifierPipe } from '../../../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../../../common/pipes/join.pipe';
import { DriverValidationHelper } from '../../../../drivers/common/helpers/driver-validation.helper';
import { DriverHelper } from '../../../../drivers/common/helpers/driver.helper';
import type { Driver } from '../../../../drivers/common/interfaces/driver';
import { DriverCategory } from '../../../../drivers/common/interfaces/driver-category';
import { DriversService } from '../../../../drivers/common/services/drivers.service';
import type { Fleet, SimplifiedFleet } from '../../interfaces/fleet';
import { FleetService } from '../../services/fleet.service';

export type FleetAssignedDriversModalData = Fleet;

type FleetAssignedDriversModalFormGroup = ReturnType<
  FleetAssignedDriversModalComponent['buildForm']
>;

export type FleetAssignedDriversModalForm = ReturnType<
  FleetAssignedDriversModalFormGroup['getRawValue']
>;

@Component({
  selector: 'app-fleet-assigned-drivers-modal',
  imports: [
    OnyxModalComponent,
    OnyxDropdownComponent,
    ReactiveFormsModule,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxIconComponent,
    FleetIdentifierPipe,
    OnyxIconComponent,
    OnyxNamePipe,
    OnyxMessageComponent,
    OnyxAutoFocusDirective,
  ],
  providers: [DriverValidationHelper, JoinPipe],
  templateUrl: './fleet-assigned-drivers-modal.component.html',
  styleUrl: './fleet-assigned-drivers-modal.component.scss',
})
export class FleetAssignedDriversModalComponent implements OnInit {
  protected readonly fleet = inject<FleetAssignedDriversModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly driversService = inject(DriversService);
  private readonly fleetService = inject(FleetService);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly driverHelper = inject(DriverHelper);
  private readonly driverValidationHelper = inject(DriverValidationHelper);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'fleet.assignedDriversModal';
  protected readonly DRIVERS_SOURCE: OnyxDropdownOptionsSource<Driver> = {
    list: (query, pagination) =>
      this.driversService
        .searchDrivers(query, {
          ...pagination,
          filters: [DriverHelper.createCategoryFilter(DriverCategory.ACTIVE)],
        })
        .pipe(
          combineLatestWith(
            this.form.valueChanges.pipe(
              startWith(null),
              map(() => this.form.getRawValue()),
            ),
          ),
          map(([result, form]) => {
            const assignedDriverUuids = chain(form).values().compact().value();
            return {
              ...result,
              options: chain(DropdownHelper.mapToOptions(result.options))
                .filter(
                  ({ value }) => !assignedDriverUuids.includes(value.uuid),
                )
                .map(
                  (option) =>
                    this.driverValidationHelper.validate({
                      context: ValidationContext.FLEET,
                      driver: option.value,
                      options: {
                        vehicle: this.fleet,
                        option,
                      },
                    }).option!,
                )
                .thru((options) =>
                  this.validationConditionsHelper.groupOptions(options),
                )
                .value(),
            };
          }),
        ),
    get: (uuid) =>
      this.driversService.getDriver(uuid).pipe(
        map((driver) => ({
          name: this.namePipe.transform(driver.driverData),
          value: driver,
        })),
      ),
    idKey: 'uuid',
  };

  protected form = this.buildForm();
  protected loading = signal(false);
  protected primaryDriver = signal<Driver | null>(null);
  protected secondaryDriver = signal<Driver | null>(null);
  protected close$ = new Subject<void>();

  protected links = computed(() => ({
    primaryDriver: CommonHelper.getPreviewLink(this.primaryDriver(), (driver) =>
      this.driverHelper.openModal(driver),
    ),
    secondaryDriver: CommonHelper.getPreviewLink(
      this.secondaryDriver(),
      (driver) => this.driverHelper.openModal(driver),
    ),
  }));

  protected swappedData = computed(() =>
    chain([this.primaryDriver(), this.secondaryDriver()])
      .compact()
      .filter(
        (driver) =>
          !!driver.assignedVehicle &&
          driver.assignedVehicle.uuid !== this.fleet.uuid,
      )
      .map((driver) => ({
        driver,
        vehicle: driver.assignedVehicle as SimplifiedFleet,
      }))
      .value(),
  );

  public ngOnInit(): void {
    const { primaryDriver, secondaryDriver } = this.fleet.drivers ?? {};
    this.form.setValue({
      primaryDriverUuid: primaryDriver?.uuid ?? null,
      secondaryDriverUuid: secondaryDriver?.uuid ?? null,
    });

    this.form.controls.primaryDriverUuid.valueChanges
      .pipe(
        startWith(this.form.controls.primaryDriverUuid.getRawValue()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((primaryDriver) => {
        const secondaryDriver =
          this.form.controls.secondaryDriverUuid.getRawValue();

        if (!primaryDriver && secondaryDriver) {
          setTimeout(() =>
            this.form.patchValue({
              primaryDriverUuid: secondaryDriver,
              secondaryDriverUuid: null,
            }),
          );
        } else {
          ValidationHelper.toggleControls(
            this.form.controls.secondaryDriverUuid,
            { enabled: !!primaryDriver, reset: !primaryDriver },
          );
        }
      });
  }

  protected openDriverModal(driver: Driver | null): void {
    if (driver) this.driverHelper.openModal(driver);
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    this.loading.set(true);
    this.fleetService
      .updateAssignedDrivers(this.fleet.uuid, form)
      .subscribe(() => {
        this.toastService.showSuccess({ message: `${this.I18N}.success` });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      primaryDriverUuid: this.fb.control<string | null>(null),
      secondaryDriverUuid: this.fb.control<string | null>({
        value: null,
        disabled: true,
      }),
    });
  }
}
